import type {
  IAuthRepository,
  AuthLoginResponse,
  AuthTokenResponse,
} from "@/application/ports/repositories";
import type { User, UserRole, UserStatus } from "@/domain/user";
import { getLocalStorageService } from "@/infrastructure/di/services";
import { STORAGE_KEYS } from "@/infrastructure/storage/constants";
import type { UserResponse } from "../generated/types.gen";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

export class AuthRepository implements IAuthRepository {
  async login(email: string, password: string): Promise<AuthLoginResponse> {
    const tokens = await unwrap(sdk.authLoginUser({ body: { email, password } }));

    // Get user profile immediately after login
    const user = await this.getUserProfile(tokens.access_token);

    return {
      tokens: {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresIn: tokens.expires_in,
      },
      user,
    };
  }

  async logout(refreshToken: string): Promise<void> {
    await unwrap(sdk.authLogout({ body: { refresh_token: refreshToken } }));
  }

  async refreshToken(refreshToken: string): Promise<AuthTokenResponse> {
    const tokens = await unwrap(sdk.authRefreshToken({ body: { refresh_token: refreshToken } }));

    return {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresIn: tokens.expires_in,
    };
  }

  async getUserProfile(accessToken: string): Promise<User> {
    const storageService = getLocalStorageService();
    // Temporarily set token for this request: during login the TokenManager
    // has no tokens yet, and the SDK auth callback falls back to storage.
    const originalToken = storageService.get<string>(STORAGE_KEYS.AUTH_TOKEN);
    storageService.set(STORAGE_KEYS.AUTH_TOKEN, accessToken);

    try {
      const profile = await unwrap(sdk.getMe());
      return this.mapBackendUserToUser(profile);
    } finally {
      // Restore original token
      if (originalToken) {
        storageService.set(STORAGE_KEYS.AUTH_TOKEN, originalToken);
      } else {
        storageService.remove(STORAGE_KEYS.AUTH_TOKEN);
      }
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await unwrap(sdk.sendForgotPasswordEmail({ body: { email: email.trim() } }));
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await unwrap(sdk.resetPassword({ body: { token: token.trim(), new_password: newPassword } }));
  }

  async confirmEmail(token: string): Promise<void> {
    await unwrap(sdk.confirmEmailVerification({ body: { token: token.trim() } }));
  }

  async sendVerificationEmail(): Promise<void> {
    await unwrap(sdk.sendVerifyEmail());
  }

  async revokeOtherSessions(): Promise<number> {
    const refreshToken =
      getLocalStorageService().get<string>(STORAGE_KEYS.AUTH_REFRESH_TOKEN) ?? "";
    const response = await unwrap(
      sdk.revokeOtherSessions({ body: { refresh_token: refreshToken } }),
    );
    return response.revoked;
  }

  async confirmEmailChange(token: string): Promise<void> {
    await unwrap(sdk.confirmEmailChange({ body: { token: token.trim() } }));
  }

  private mapBackendUserToUser(backendUser: UserResponse): User {
    const fullName = backendUser.full_name || backendUser.email;
    const role: UserRole = "viewer";
    const status: UserStatus = backendUser.is_active ? "active" : "inactive";

    return {
      id: backendUser.uuid,
      name: fullName,
      email: backendUser.email,
      role,
      status,
      joinDate: backendUser.created_at ? new Date(backendUser.created_at) : undefined,
      avatar: backendUser.profile_picture_signed_url || undefined,
      profile_picture_signed_url: backendUser.profile_picture_signed_url || undefined,
      first_name: backendUser.first_name,
      last_name: backendUser.last_name,
      is_verified: backendUser.is_verified,
      last_login_at: backendUser.last_login_at,
      uuid: backendUser.uuid,
    };
  }
}
